import React from "react"
import {useTranslation} from "react-i18next"

// Models
import {Signal} from "../../../records/models"
import {
    Beat,
    BeatClass,
    ENVELOPE_SIDE,
    LEAD_ORDER,
    leadKeyOf,
    leadLabel,
    LEAD_POINT_KEYS,
    OrderProblem,
    orderProblems,
    PointKey,
    POINT_KEYS,
    sampleMs,
    snapToSample,
    startingPoint,
} from "../../model"

import {checkBeat, failureCount, leadFloors} from "../../envelopeCheck"
import {msToSampleZeroBased} from "../../globalCsv"
import {modalOpen} from "../../useAnnotation"
import useEcgPxPerMm from "../../../records/components/modern/ModernEcgGrid/useEcgPxPerMm"

// Styles
import useStyles from "./styles"

/**
 * The beat surface: an ordinary ECG, with the six boundaries drawn on it.
 *
 * TRUE PAPER SCALE, ONE GAIN
 *
 * An earlier version fitted the gain to whichever wave was selected and
 * magnified only the span around it. It made the P wave tall, and it also made
 * the trace a lie: the span stops at the midpoint to the next boundary, which
 * cuts through the middle of the P, so the wave was drawn magnified on one side
 * of the cut and not the other and came out visibly lopsided. A deformed wave
 * is worse than a small one — a technician cannot mark what they cannot trust.
 *
 * So this draws what every other ECG screen draws: millimetre grid, 25 mm/s,
 * 10 mm/mV, one gain for every lead and every instant. Speed and gain move in
 * the standard steps, and faster paper is how a boundary gets resolved, exactly
 * as it is on a real recorder.
 *
 * Precision does not come from magnification. At 25 mm/s a pixel is ~10.6 ms,
 * so the arrow keys move the mark one SAMPLE at a time whatever the screen is
 * showing, and the readout states the position in milliseconds. The eye finds
 * the wave; the keyboard and the number place the mark.
 */

type Props = {
    signals: Signal[]
    beats: Beat[]
    /** 1-based index of the beat being worked on. */
    current: number
    selected: PointKey
    onSelect(beat: number, key: PointKey): void
    onMove(beat: number, key: PointKey, ms: number | null): void
    /** Local time of the last autosave, or null if nothing is stored yet. */
    savedAt?: string | null
    /** True when the browser refused to store — the work is only in memory. */
    saveFailed?: boolean
    /** Where the last save landed: the server, or only this browser. */
    savedTo?: "server" | "local" | null
    /** Whether the server's labels are still on their way, could not be fetched, or are at another rate. */
    labelsStatus?: "idle" | "loading" | "ready" | "failed" | "rate-mismatch"
    /**
     * The record's own sample rate (recordSampleRate), or null when it is not
     * known — then nothing is placed, and the screen says why.
     */
    sampleRate?: number | null
    rateProblem?: "loading" | "missing" | "mixed" | null
    /** The server's rate, when it differs from the signal's. */
    serverRate?: number | null
    /** Beats where an old browser copy lost to newer work on the server. */
    conflicts?: number
    /** The last save to the server failed and is being retried. */
    saveFailing?: boolean
    /** The server refused the last save, with this status; not retried until the next edit. */
    saveRefused?: number | null
    /** A drag is one gesture, for undo. */
    onDragStart?(): void
    onDragEnd?(): void
}

/** A time for the screen: whole ms, or one decimal at rates where a sample is not. */
function fmt(ms: number): string {
    return String(Math.round(ms * 10) / 10)
}

/**
 * Whether the orange per-lead reference is drawn — a display preference only,
 * kept in this browser like the other cardiolyse.ui-* settings. Shown unless
 * the technician hid it.
 */
export const REFERENCE_PREF_KEY = "cardiolyse.ui-annotation-reference";

function readShowReference(): boolean {
    try {
        return window.localStorage.getItem(REFERENCE_PREF_KEY) !== "hidden"
    } catch (error) {
        return true
    }
}

function writeShowReference(show: boolean) {
    try {
        window.localStorage.setItem(REFERENCE_PREF_KEY, show ? "shown" : "hidden")
    } catch (error) {
        /* private mode or blocked storage: the toggle still works for this page */
    }
}

/** How far around its R peak a beat's marks may be placed, at most. */
const NEIGHBOURHOOD_MS = 600;

/** Pointer travel, in px, before a press on a line counts as dragging it. */
const DRAG_THRESHOLD_PX = 3;

/** P, QRS or T — for any key of either mode. */
function waveOf(key: string): string {
    return key.indexOf("qrs") === 0 ? "QRS" : key.charAt(0) === "p" ? "P" : "T"
}

function isPeak(key: string): boolean {
    return key.indexOf("Peak") !== -1
}

/**
 * Head and foot margins, and the calibration bay, in millimetres.
 *
 * Millimetres rather than pixels because the pixels-per-millimetre is not a
 * constant: it comes from `useEcgPxPerMm`, which returns whatever the reviewer
 * measured against a ruler on this particular screen, divided by the current
 * `devicePixelRatio`. Hard-coding 96/25.4 — which this did — draws a grid that
 * is the right size only on an uncalibrated display at 100 % zoom, and silently
 * disagrees with the review stage everywhere else.
 */
const TOP_BAND_MM = 10;
const BOTTOM_BAND_MM = 5;

/**
 * Paper speeds, in mm/s.
 *
 * 25 and 50 are the recorder's own settings. 100 and 200 are not paper speeds a
 * machine offers, but this is a measuring screen: at 200 mm/s a pixel covers
 * 1.3 ms, finer than the 2 ms between samples, which is what it takes to SEE
 * which sample a boundary belongs to rather than trust the number alone.
 */
const SPEEDS = [25, 50, 100, 200];
const GAINS = [5, 10, 20, 40];

const OVERVIEW_H = 86;
const GUTTER = 38;

/**
 * Room for the calibration pulse, in millimetres, before the trace starts.
 *
 * Every recorder prints one and every technician reads it first: a 1 mV step,
 * 5 mm wide. It is the only thing on the screen that proves the gain rather
 * than asserting it, and because a 1 mV step is exactly half a row at any gain
 * here, it also shows at a glance how much room a lead has.
 */
const CAL_MM = 8;
/**
 * Row pitch follows the gain: enough paper for a 1 mV swing either way.
 *
 * A fixed pitch is wrong in both directions. At 13 mm it cut the R waves flat,
 * and a clipped QRS is not an ECG; at 20 mm the leads are honest but twelve of
 * them come to ~950 px and only eight fit the pane.
 *
 * Tying the two together gives the technician a real choice in the gain they
 * already understand: at 5 mm/mV all twelve leads fit at once, which is what
 * the envelope judgement wants; at 10 mm/mV and above the trace is bigger and
 * the leads scroll, which is what examining one of them wants.
 *
 * @param gain mm per mV
 */
function rowMmFor(gain: number): number {
    return gain * 2
}

/** Lead label, tolerating the aVR/AVR spelling difference between sources. */
const leadName = leadLabel;

/** Signals in diagnostic order, whatever order they arrived in. */
function ordered(signals: Signal[]): Signal[] {
    const byLead: {[lead: string]: Signal} = {};

    signals.forEach(signal => {
        byLead[leadKeyOf(signal.lead)] = signal
    });

    const known = LEAD_ORDER.map(lead => byLead[lead]).filter(Boolean);
    const rest = signals.filter(s => LEAD_ORDER.indexOf(leadKeyOf(s.lead)) === -1);

    return known.concat(rest)
}

export default function AnnotationView(props: Props) {
    const {
        signals, beats, current, selected, onSelect, onMove,
        savedAt, saveFailed, savedTo, labelsStatus, onDragStart, onDragEnd,
    } = props;

    /* the record's own rate; 0 while not known, and then nothing is placed */
    const sampleRate = props.sampleRate && props.sampleRate > 0 ? props.sampleRate : 0;

    const classes = useStyles();
    const {t} = useTranslation();

    const workRef = React.useRef<HTMLDivElement | null>(null);
    const workCanvas = React.useRef<HTMLCanvasElement | null>(null);
    const overviewCanvas = React.useRef<HTMLCanvasElement | null>(null);

    const [paneW, setPaneW] = React.useState(1100);

    /**
     * How far the work pane is scrolled sideways.
     *
     * Tracked because the lead names and the calibration pulse are painted into
     * the canvas, which scrolls — so past the first screenful the gutter was off
     * to the left and the rows were anonymous. A technician cannot judge an
     * envelope across leads they can no longer name, so the gutter is redrawn
     * pinned to this offset and stays put.
     */
    const [scrollX, setScrollX] = React.useState(0);
    const [speed, setSpeed] = React.useState(25);

    /**
     * Opens at 5 mm/mV, not the diagnostic 10.
     *
     * Row pitch follows the gain, so twelve leads come to ~510 px at 5 and
     * ~960 px at 10 — and the work pane, under an overview strip, the boundary
     * bar, the controls and the footer, is nearer 500. At 10 the technician sees
     * eight leads and has to scroll, which makes the one judgement this screen
     * exists for — the EARLIEST onset and LATEST offset over ALL twelve —
     * impossible without remembering what scrolled past.
     *
     * So the default is the setting that fits the task, and raising the gain to
     * inspect a single lead closely is the deliberate act.
     */
    const [gain, setGain] = React.useState(5);

    /*
     * The orange per-lead reference, shown or hidden. Display only: it changes
     * what is drawn, never a mark, a status or what is saved. Remembered
     * across beats and records.
     */
    const [showReference, setShowReference] = React.useState(readShowReference);

    const toggleReference = React.useCallback(() => {
        setShowReference(show => {
            writeShowReference(!show);

            return !show
        })
    }, []);
    const [dragging, setDragging] = React.useState<PointKey | null>(null);
    /* the drag in hand: where on the line it was grabbed, and whether it moved */
    const dragRef = React.useRef<{key: PointKey, grabMs: number, startX: number, moved: boolean} | null>(null);
    /* a click that could not be honoured, said in the footer until the next one */
    const [hint, setHint] = React.useState<string | null>(null);

    const leads = React.useMemo(() => ordered(signals), [signals]);
    const beat = beats[current - 1];


    /**
     * Does each mark actually contain every lead?
     *
     * The floors are the expensive half and they do not really move, so they are
     * memoised on the signal rather than on the marks — otherwise every arrow
     * press would re-scan the whole record to re-derive the same noise estimate.
     */
    const floors = React.useMemo(
        () => leadFloors(leads, beats, sampleRate || 1),
        /* eslint-disable-next-line react-hooks/exhaustive-deps */
        [leads, beats.length],
    );

    const beatCheck = React.useMemo(
        () => (beat && sampleRate ? checkBeat(leads, floors, beat, sampleRate) : null),
        [leads, floors, beat, sampleRate],
    );

    /** How many beats in the whole record still have a mark that misses a lead. */
    const uncontained = React.useMemo(
        () => (sampleRate
            ? beats.filter(b => failureCount(checkBeat(leads, floors, b, sampleRate)) > 0).length
            : 0),
        [leads, floors, beats, sampleRate],
    );

    /** Any per-lead reference in this record — or there is nothing to show or hide. */
    const hasReference = React.useMemo(() => beats.some(b => Boolean(b.reference)), [beats]);

    /** What breaks the order on the beat in hand, against its neighbours. */
    const problems = React.useMemo(
        () => (beat ? orderProblems(beat, beats[current] || null, beats[current - 2] || null) : []),
        [beat, beats, current],
    );

    /** How many beats in the record break the order. */
    const outOfOrder = React.useMemo(
        () => beats.filter((b, i) => orderProblems(b, beats[i + 1] || null, beats[i - 1] || null).length > 0).length,
        [beats],
    );

    /** Is this mark one of the two a problem names? */
    const involved = React.useCallback(
        (key: PointKey) => problems.some(p => p.key === key || p.other === key),
        [problems],
    );

    const samples = leads.length ? Math.min.apply(null, leads.map(l => l.data.length)) : 0;
    const durationMs = sampleRate && samples ? (samples * 1000) / sampleRate : 0;
    /** The record's last sample: nothing is placed past it. */
    const endMs = sampleRate && samples ? ((samples - 1) * 1000) / sampleRate : 0;

    /**
     * The same millimetre the review stage uses.
     *
     * Not 96/25.4: that is only right on a screen nobody has calibrated, at
     * 100 % zoom. `useEcgPxPerMm` returns the density the reviewer measured
     * with a ruler through <ModernScaleDialog/>, divided by the live
     * `devicePixelRatio`, and re-renders when either moves — so a square here
     * is the same size as a square on the review grid, which is the only way
     * a measurement made on one means anything on the other.
     */
    const {pxPerMm: PX_PER_MM} = useEcgPxPerMm();

    const TOP_BAND = TOP_BAND_MM * PX_PER_MM;
    const BOTTOM_BAND = BOTTOM_BAND_MM * PX_PER_MM;

    /**
     * Pixels per raw sample unit.
     *
     * The samples are NOT millivolts. They arrive in hundredths of a
     * millimetre at standard gain — about 1000 to the millivolt — which is the
     * same conversion `ModernEcgGrid` draws with:
     *
     *     millimetres = value * (gain / 10) / 100
     *
     * Treating them as millivolts, which this did until it was checked against
     * the review grid, scales every sample by about a thousand: a 1 mV R wave
     * is drawn some 38000 px tall and the trace is a vertical scribble. It
     * looked right only because the test harness was feeding millivolts, so
     * the harness was wrong in exactly the way that hid it.
     */
    const pxPerUnit = ((gain / 10) / 100) * PX_PER_MM;
    /** One millivolt on screen — for the calibration pulse. */
    const pxPerMv = gain * PX_PER_MM;
    const rowH = rowMmFor(gain) * PX_PER_MM;


    const traceX0 = GUTTER + CAL_MM * PX_PER_MM;

    /* Paper scale. Both axes real millimetres, which is the whole point. */
    const pxPerMs = (speed * PX_PER_MM) / 1000;
    const msPerPx = 1 / pxPerMs;

    /* Wide enough for the calibration bay AND the whole trace. Measuring from
       GUTTER instead of from where the trace actually starts left the last
       ~8 mm of paper — about 190 ms at 25 mm/s — off the end of the canvas,
       where the final beat's T end could not be seen, clicked or dragged.
       This is the width of the PAPER (the scroll range); the canvas itself is
       only as wide as the pane, see the drawing effect. */
    const canvasW = Math.max(paneW, Math.round(traceX0 + durationMs * pxPerMs) + 12);
    const canvasH = leads.length * rowH + TOP_BAND + BOTTOM_BAND;


    const xOf = React.useCallback(
        (ms: number) => traceX0 + ms * pxPerMs,
        [traceX0, pxPerMs],
    );
    const msOf = React.useCallback(
        (x: number) => (x - traceX0) * msPerPx,
        [traceX0, msPerPx],
    );

    /**
     * A pixel, snapped to the sample it is nearest.
     *
     * A boundary can only honestly sit ON a sample — there is no signal between
     * two of them — and the file says so: `time_ms = sample * 2` at 500 Hz.
     * Rounding a click to the nearest MILLISECOND instead, which this did, put
     * marks between samples: a click landing on 847 ms was written as
     * `onset_sample 424, onset_ms 847`, and 424 × 2 is 848. The file
     * contradicted itself, by an amount that depended on the paper speed,
     * because at 25 mm/s one pixel is 10.58 ms — more than five samples — so
     * the error was invisible on screen and only appeared in the export.
     *
     * Snapping here also makes the mark reproducible: the same click at 25 and
     * at 200 mm/s now yields the same sample, where before the two speeds
     * rounded to different milliseconds.
     *
     * @param x pixels from the left of the canvas
     */
    const sampleMsOf = React.useCallback(
        (x: number) => snapToSample(msOf(x), sampleRate || 1, endMs),
        [msOf, sampleRate, endMs],
    );

    React.useEffect(() => {
        const pane = workRef.current;

        /*
         * Keep the last good width; never store a zero.
         *
         * `clientWidth` is 0 while the pane is still laying out, and whenever
         * it is display:none. Storing that made `canvasW` zero, so the canvas
         * had no backing store and the screen went blank — and under the wave
         * zoom, where the pane width IS the time scale, it also divided the
         * window across no pixels at all.
         */
        function measure() {
            if (workRef.current && workRef.current.clientWidth > 0) {
                setPaneW(workRef.current.clientWidth)
            }
        }

        function onScroll() {
            if (workRef.current) {
                setScrollX(workRef.current.scrollLeft)
            }
        }

        measure();
        window.addEventListener("resize", measure);

        if (pane) {
            pane.addEventListener("scroll", onScroll)
        }

        return () => {
            window.removeEventListener("resize", measure);

            if (pane) {
                pane.removeEventListener("scroll", onScroll)
            }
        }
    }, []);

    /** Bumped when the overview strip picks the beat already in hand. */
    const [recentre, setRecentre] = React.useState(0);
    const lastSpeed = React.useRef(speed);
    const lastRecentre = React.useRef(recentre);

    /* the beat as drawn now, for the scroll effect without making its every
       value change a reason to scroll */
    const beatRef = React.useRef(beat);

    beatRef.current = beat;

    /*
     * Keep the boundary in hand on screen as the SELECTION or the speed moves.
     *
     * Not as its value moves: a drag near the edge used to scroll the pane
     * under the pointer on every move, so the mark ran on into other beats.
     * A drag never scrolls; an arrow press that walks a mark off screen is
     * brought back the next time the selection or speed changes.
     *
     * The paper's width is set in the markup (the wrapper's style), so by the
     * time this runs the pane already has the full scroll range — on a jump
     * from 25 to 200 mm/s the browser used to clamp the scroll to the old
     * width, leaving the beat being edited off screen.
     *
     * After a speed change the old scroll position means nothing at the new
     * scale, so the boundary is centred rather than merely kept in view.
     */
    React.useEffect(() => {
        const pane = workRef.current;
        const now = beatRef.current;

        if (!pane || !now || dragRef.current) {
            return
        }

        const focusMs = now.points[selected] === null
            ? now.rPeakMs
            : (now.points[selected] as number);
        const x = xOf(focusMs);
        const centre = lastSpeed.current !== speed || lastRecentre.current !== recentre;

        lastSpeed.current = speed;
        lastRecentre.current = recentre;

        if (centre || x < pane.scrollLeft + 80 || x > pane.scrollLeft + pane.clientWidth - 80) {
            pane.scrollLeft = Math.max(0, x - pane.clientWidth / 2)
        }
    }, [current, selected, xOf, speed, canvasW, recentre]);

    /* size the backing store — only when it changes, since setting a canvas's
       width reallocates and clears it — and reset the transform */
    function fit(canvas: HTMLCanvasElement, w: number, h: number) {
        const dpr = window.devicePixelRatio || 1;
        const bw = Math.round(w * dpr);
        const bh = Math.round(h * dpr);

        if (canvas.width !== bw || canvas.height !== bh) {
            canvas.width = bw;
            canvas.height = bh;
            canvas.style.width = `${w}px`;
            canvas.style.height = `${h}px`
        }

        canvas.getContext("2d")!.setTransform(dpr, 0, 0, dpr, 0, 0)
    }

    /* ---------------------------------------------------------------- *
     * The trace
     * ---------------------------------------------------------------- */
    React.useEffect(() => {
        const canvas = workCanvas.current;

        if (!canvas || !beat || !leads.length || !sampleRate) {
            return
        }

        /*
         * The canvas is the size of the PANE, not of the paper.
         *
         * A paper-sized canvas came to 9549 x 4606 CSS px on a ten second
         * record at 200 mm/s — about 176 Mpx at DPR 2, reallocated on every
         * scroll and key — and past 32,767 px (a 60 s record at 200 mm/s) the
         * browser drew nothing at all while the canvas stayed clickable. Now
         * the canvas stays put over the visible part of the paper (sticky,
         * inside a wrapper as wide as the paper), the drawing is shifted by
         * the scroll, and only what is in view is drawn.
         */
        const viewW = Math.min(paneW, canvasW);
        const left = workRef.current ? workRef.current.scrollLeft : scrollX;
        const right = left + viewW;

        fit(canvas, viewW, canvasH);

        const ctx = canvas.getContext("2d")!;

        ctx.clearRect(0, 0, viewW, canvasH);
        ctx.fillStyle = "#fff";
        ctx.fillRect(0, 0, viewW, canvasH);
        ctx.translate(-left, 0);

        /* Paper only where there is signal — grid running on past the end of
           the recording reads as more record than there is. */
        const paperEnd = Math.min(canvasW, traceX0 + durationMs * pxPerMs, right);
        /* the first visible millimetre of the grid */
        const mm0 = Math.max(0, Math.floor((left - GUTTER) / PX_PER_MM));

        /* ECG paper: 1 mm minor, 5 mm major */
        ctx.lineWidth = 1;
        ctx.strokeStyle = "rgba(220, 38, 38, .16)";
        ctx.beginPath();

        for (let mm = mm0; GUTTER + mm * PX_PER_MM < paperEnd; mm += 1) {
            if (mm % 5 === 0) { continue }

            const x = GUTTER + mm * PX_PER_MM;

            ctx.moveTo(x, 0);
            ctx.lineTo(x, canvasH)
        }

        for (let mm = 0; mm * PX_PER_MM < canvasH; mm += 1) {
            if (mm % 5 === 0) { continue }

            const y = mm * PX_PER_MM;

            ctx.moveTo(Math.max(GUTTER, left), y);
            ctx.lineTo(paperEnd, y)
        }

        ctx.stroke();

        ctx.strokeStyle = "rgba(220, 38, 38, .38)";
        ctx.beginPath();

        for (let mm = mm0 - (mm0 % 5); GUTTER + mm * PX_PER_MM < paperEnd; mm += 5) {
            const x = GUTTER + mm * PX_PER_MM;

            ctx.moveTo(x, 0);
            ctx.lineTo(x, canvasH)
        }

        for (let mm = 0; mm * PX_PER_MM < canvasH; mm += 5) {
            const y = mm * PX_PER_MM;

            ctx.moveTo(Math.max(GUTTER, left), y);
            ctx.lineTo(paperEnd, y)
        }

        ctx.stroke()

        /* The traces. One gain, every lead, every instant. */
        leads.forEach((signal, row) => {
            const mid = TOP_BAND + row * rowH + rowH / 2;

            /*
             * No clipping to the row.
             *
             * Clipping cut every R wave flat, because at 10 mm/mV a tall R is
             * simply taller than its band. Paper does not clip either — a big
             * deflection runs into the lead above and a reader reads it anyway,
             * which is the convention this follows.
             *
             */
            ctx.save();

            ctx.strokeStyle = "#111";
            ctx.lineWidth = 1.1;
            ctx.beginPath();

            let started = false;
            /* only the samples in view, with one either side to reach the edge */
            const first = Math.max(0, Math.floor(msOf(left) * sampleRate / 1000) - 1);
            const last = Math.min(signal.data.length - 1, Math.ceil(msOf(right) * sampleRate / 1000) + 1);

            for (let i = first; i <= last; i += 1) {
                const x = xOf((i * 1000) / sampleRate);
                const y = mid - signal.data[i] * pxPerUnit;

                if (!started) {
                    ctx.moveTo(x, y);
                    started = true
                } else {
                    ctx.lineTo(x, y)
                }
            }

            ctx.stroke();
            ctx.restore();

            /*
             * The calibration pulse: 1 mV up, 5 mm wide, then back.
             * Drawn per lead, as a recorder prints it, so the gain is
             * demonstrated on every row rather than asserted in a toolbar.
             *
             * THE NAME IS PINNED TO THE SCROLL. THE PULSE IS NOT.
             *
             * Both used to ride along, and that put a calibration pulse in
             * the middle of the recording: scroll to 4.6 s and there it was,
             * punched through the paper on every row, asserting a 1 mV step
             * that is not in the signal at 4.6 s. A recorder prints the
             * pulse ONCE, at the start, and it scrolls away like everything
             * else on the paper.
             *
             * It only showed up after a speed change because at 25 mm/s a
             * ten second record fits the pane and there is nothing to
             * scroll; at 100 mm/s it is 3860 px and there is.
             *
             * The lead name genuinely does have to stay put — an envelope
             * cannot be judged across leads that can no longer be named —
             * so the pinned strip is now just the name column.
             */
            /* the scroll position as read NOW (above) rather than from state:
               after a speed change the state is a frame behind */
            const atX = left;

            ctx.fillStyle = "#fff";
            ctx.fillRect(atX, mid - rowH / 2, GUTTER - 2, rowH);

            /* A rule down its right edge, so once the paper has scrolled
               under it the strip reads as a fixed column and not as a gap
               torn in the trace. */
            if (atX > 0) {
                ctx.strokeStyle = "rgba(107, 114, 128, .35)";
                ctx.lineWidth = 1;
                ctx.beginPath();
                ctx.moveTo(atX + GUTTER - 2, mid - rowH / 2);
                ctx.lineTo(atX + GUTTER - 2, mid + rowH / 2);
                ctx.stroke()
            }

            const calX = GUTTER + 1.5 * PX_PER_MM;
            const calTop = mid - pxPerMv;

            ctx.strokeStyle = "#111";
            ctx.lineWidth = 1.1;
            ctx.beginPath();

            ctx.moveTo(calX, mid);
            ctx.lineTo(calX, calTop);
            ctx.lineTo(calX + 5 * PX_PER_MM, calTop);
            ctx.lineTo(calX + 5 * PX_PER_MM, mid);
            ctx.lineTo(traceX0, mid);
            ctx.stroke();

            ctx.fillStyle = "#374151";
            ctx.font = "600 11px -apple-system, Segoe UI, Roboto, sans-serif";
            ctx.textBaseline = "middle";
            ctx.fillText(leadName(signal.lead), atX + 5, mid)
        });

        /* Seconds along the head margin — where in the record this is, without
           counting large squares across a ten second strip. */
        ctx.fillStyle = "#9ca3af";
        ctx.font = "10px -apple-system, Segoe UI, Roboto, sans-serif";
        ctx.textBaseline = "alphabetic";
        ctx.textAlign = "left";

        const secStep = speed >= 100 ? 0.2 : speed >= 50 ? 0.5 : 1;

        for (let s = 0; s * 1000 <= durationMs; s += secStep) {
            const x = xOf(s * 1000);

            if (x > right) {
                break
            }

            ctx.fillText(`${Math.round(s * 10) / 10}s`, x + 2, 11)
        }
        /* The R peak of the beat in hand — the anchor, not a mark. */
        const rx = xOf(beat.rPeakMs);

        ctx.strokeStyle = "rgba(107, 114, 128, .55)";
        ctx.lineWidth = 1;
        ctx.setLineDash([2, 4]);
        ctx.beginPath();
        ctx.moveTo(rx, 0);
        ctx.lineTo(rx, canvasH);
        ctx.stroke();
        ctx.setLineDash([]);

        /*
         * The per-lead model's marks, when the record came with them —
         * REFERENCE, drawn under the global lines and never editable.
         *
         * Each row gets its own lead's marks, as short ticks inside the row,
         * and its peaks as small rings on the trace. The ticks for the
         * boundary in hand are drawn stronger on every row, so the technician
         * can see straight away which lead starts earliest or ends latest —
         * the one the global mark has to reach.
         */
        if (beat.reference && showReference) {
            const reference = beat.reference;

            leads.forEach((signal, row) => {
                const marks = reference[leadKeyOf(signal.lead)];

                if (!marks) {
                    return
                }

                const top = TOP_BAND + row * rowH;
                const mid = top + rowH / 2;

                LEAD_POINT_KEYS.forEach(key => {
                    const ms = marks[key];

                    if (ms === null) {
                        return
                    }

                    const x = xOf(ms);

                    if (isPeak(key)) {
                        const i = Math.min(signal.data.length - 1, Math.max(0, msToSampleZeroBased(ms, sampleRate)));

                        ctx.strokeStyle = "rgba(194, 65, 12, .55)";
                        ctx.lineWidth = 1;
                        ctx.beginPath();
                        ctx.arc(x, mid - signal.data[i] * pxPerUnit, 2.5, 0, Math.PI * 2);
                        ctx.stroke();

                        return
                    }

                    const inHand = key === selected;

                    ctx.strokeStyle = inHand ? "rgba(194, 65, 12, .95)" : "rgba(194, 65, 12, .4)";
                    ctx.lineWidth = inHand ? 2 : 1;
                    ctx.beginPath();
                    ctx.moveTo(x, top + rowH * 0.2);
                    ctx.lineTo(x, top + rowH * 0.8);
                    ctx.stroke()
                })
            })
        }

        /* The six boundaries, each one line across every lead. */
        POINT_KEYS.forEach(key => {
            const ms = beat.points[key];

            if (ms === null) {
                return
            }

            const x = xOf(ms);
            const isSel = key === selected;

            /* a mark that breaks the order is red, live, as it moves */
            ctx.strokeStyle = involved(key)
                ? (isSel ? "#dc2626" : "rgba(220, 38, 38, .75)")
                : (isSel ? "#1d4ed8" : "rgba(29, 78, 216, .4)");
            ctx.lineWidth = isSel ? 2 : 1;
            ctx.setLineDash(waveOf(key) === "QRS" ? [] : [5, 3]);
            ctx.beginPath();
            ctx.moveTo(x, 0);
            ctx.lineTo(x, canvasH);
            ctx.stroke();
            ctx.setLineDash([]);

            /* Only the selected one is named here. At 25 mm/s the six sit
               within about 45 px of each other and six labels became one
               smear; the bar below names them all, legibly. */
            if (isSel) {
                ctx.fillStyle = "#1d4ed8";
                ctx.font = "600 10px -apple-system, Segoe UI, Roboto, sans-serif";
                ctx.textAlign = "center";
                ctx.fillText(
                    `${t(`annotation.point.${key}`)} · ${fmt(ms)} ms`,
                    x,
                    canvasH - 4,
                );
                ctx.textAlign = "left"
            }

            if (beat.uncertain.indexOf(key) !== -1) {
                ctx.fillStyle = "#b45309";
                ctx.beginPath();
                ctx.arc(x, 7, 3.5, 0, Math.PI * 2);
                ctx.fill()
            }
        })
    }, [beat, leads, selected, canvasW, canvasH, rowH, pxPerMv, pxPerUnit, xOf, msOf, traceX0, speed, sampleRate, durationMs, pxPerMs, PX_PER_MM, TOP_BAND, BOTTOM_BAND, scrollX, paneW, t, involved, showReference]);

    /* ---------------------------------------------------------------- *
     * The overview strip
     * ---------------------------------------------------------------- */
    React.useEffect(() => {
        const canvas = overviewCanvas.current;

        if (!canvas || !leads.length || !durationMs || !sampleRate) {
            return
        }

        fit(canvas, paneW, OVERVIEW_H);

        const ctx = canvas.getContext("2d")!;

        ctx.clearRect(0, 0, paneW, OVERVIEW_H);

        const strip = leads[1] || leads[0];
        const pxPerMsOv = (paneW - 8) / durationMs;
        const mid = OVERVIEW_H / 2 + 4;

        let peak = 0;

        for (let i = 0; i < strip.data.length; i += 1) {
            const v = Math.abs(strip.data[i]);

            if (v > peak) {
                peak = v
            }
        }

        const g = peak > 0 ? (OVERVIEW_H * 0.3) / peak : 1;

        ctx.strokeStyle = "#111";
        ctx.lineWidth = 1;
        ctx.beginPath();

        const step = Math.max(1, Math.floor(strip.data.length / (paneW * 2)));

        for (let i = 0; i < strip.data.length; i += step) {
            const x = 4 + ((i * 1000) / sampleRate) * pxPerMsOv;
            const y = mid - strip.data[i] * g;

            if (i === 0) {
                ctx.moveTo(x, y)
            } else {
                ctx.lineTo(x, y)
            }
        }

        ctx.stroke();

        beats.forEach((b, i) => {
            const x = 4 + b.rPeakMs * pxPerMsOv;
            const isCur = i + 1 === current;

            ctx.fillStyle = isCur
                ? "#1d4ed8"
                : b.status === "confirmed"
                    ? "rgba(21, 128, 61, .75)"
                    : b.status === "corrected"
                        ? "rgba(29, 78, 216, .55)"
                        : "rgba(156, 163, 175, .9)";

            ctx.fillRect(x - (isCur ? 1.5 : 1), 4, isCur ? 3 : 2, OVERVIEW_H - 8)
        })
    }, [beats, current, leads, paneW, durationMs, sampleRate]);

    /* ---------------------------------------------------------------- *
     * Pointer
     * ---------------------------------------------------------------- */
    function nearest(x: number): PointKey | null {
        if (!beat) {
            return null
        }

        let best: PointKey | null = null;
        let bestD = 8;

        POINT_KEYS.forEach(key => {
            const ms = beat.points[key];

            if (ms === null) {
                return
            }

            const d = Math.abs(xOf(ms) - x);

            if (d < bestD) {
                bestD = d;
                best = key
            }
        });

        return best
    }

    /** A pointer event's x on the PAPER: the canvas only covers the visible part. */
    function paperX(clientX: number): number {
        const canvas = workCanvas.current;
        const pane = workRef.current;
        const rect = canvas ? canvas.getBoundingClientRect() : {left: 0};

        return clientX - rect.left + (pane ? pane.scrollLeft : 0)
    }

    /**
     * Where this beat's marks may go: between the neighbouring beats' R peaks,
     * and no further than NEIGHBOURHOOD_MS from its own — inside the record.
     * A click past that would put this beat's mark in another beat.
     */
    function bounds(): {lo: number, hi: number} {
        const prev = beats[current - 2];
        const next = beats[current];
        const lo = Math.max(0, beat.rPeakMs - NEIGHBOURHOOD_MS, prev ? prev.rPeakMs : 0);
        const hi = Math.min(endMs, beat.rPeakMs + NEIGHBOURHOOD_MS, next ? next.rPeakMs : endMs);

        return {lo: lo, hi: hi}
    }

    function handleWorkDown(event: React.MouseEvent<HTMLCanvasElement>) {
        /* the primary button only — a right click is not a placement */
        if (event.button !== 0 || !beat || !sampleRate) {
            return
        }

        const x = paperX(event.clientX);

        setHint(null);

        /* the lead names, the calibration pulse and past the record are not paper */
        if (x < traceX0 || msOf(x) > endMs) {
            return
        }

        const hit = nearest(x);

        if (hit) {
            /*
             * Grab, don't jump. The drag keeps the distance between the
             * pointer and the line, and only counts once the pointer has
             * travelled a few pixels — a click on a line selects it and
             * changes nothing.
             */
            onSelect(current, hit);
            dragRef.current = {key: hit, grabMs: (beat.points[hit] as number) - msOf(x), startX: event.clientX, moved: false};
            setDragging(hit);

            return
        }

        const ms = sampleMsOf(x);
        const {lo, hi} = bounds();

        if (ms < lo || ms > hi) {
            setHint(t("annotation.outside_beat"));

            return
        }

        onMove(current, selected, ms)
    }

    React.useEffect(() => {
        if (!dragging) {
            return
        }

        function end() {
            const drag = dragRef.current;

            if (drag && drag.moved && onDragEnd) {
                onDragEnd()
            }

            dragRef.current = null;
            setDragging(null)
        }

        function move(event: MouseEvent) {
            const drag = dragRef.current;

            /* the button came up outside the window: the drag is over */
            if (!drag || event.buttons === 0) {
                end();

                return
            }

            if (!drag.moved) {
                if (Math.abs(event.clientX - drag.startX) < DRAG_THRESHOLD_PX) {
                    return
                }

                drag.moved = true;

                if (onDragStart) {
                    onDragStart()
                }
            }

            const {lo, hi} = bounds();
            const to = Math.min(hi, Math.max(lo, msOf(paperX(event.clientX)) + drag.grabMs));

            onMove(current, drag.key, snapToSample(to, sampleRate, endMs))
        }

        window.addEventListener("mousemove", move);
        window.addEventListener("mouseup", end);

        return () => {
            window.removeEventListener("mousemove", move);
            window.removeEventListener("mouseup", end)
        }
        /* bounds and paperX read the same values listed here */
        /* eslint-disable-next-line react-hooks/exhaustive-deps */
    }, [dragging, current, msOf, sampleRate, endMs, beats, onMove, onDragStart, onDragEnd]);

    function handleOverviewDown(event: React.MouseEvent<HTMLCanvasElement>) {
        if (!beats.length || !durationMs) {
            return
        }

        const rect = event.currentTarget.getBoundingClientRect();
        const ms = ((event.clientX - rect.left - 4) / (paneW - 8)) * durationMs;

        let nearestBeat = 1;
        let bestD = Infinity;

        beats.forEach((b, i) => {
            const d = Math.abs(b.rPeakMs - ms);

            if (d < bestD) {
                bestD = d;
                nearestBeat = i + 1
            }
        });

        onSelect(nearestBeat, POINT_KEYS[0]);

        /* picking the beat already in hand changes no state, so nothing would
           bring it back into view — ask for that explicitly */
        if (nearestBeat === current) {
            setRecentre(n => n + 1)
        }
    }

    /* View keys. Speed and gain here; the model's keys live in useAnnotation. */
    React.useEffect(() => {
        function onKey(event: KeyboardEvent) {
            const node = event.target as HTMLElement | null;
            const tag = node && node.tagName ? node.tagName.toLowerCase() : "";

            if (tag === "input" || tag === "textarea" || tag === "select" || (node && node.isContentEditable)) {
                return
            }

            /* bare keys only: Ctrl + and Ctrl − are the browser's zoom, and
               used to change the gain as well; none behind a dialog; and a
               held key steps once, not through every setting */
            if (event.ctrlKey || event.metaKey || event.altKey || event.repeat || modalOpen()) {
                return
            }

            if (event.key === "+" || event.key === "=") {
                setGain(g => GAINS[Math.min(GAINS.length - 1, GAINS.indexOf(g) + 1)]);
                event.preventDefault()
            } else if (event.key === "-" || event.key === "_") {
                setGain(g => GAINS[Math.max(0, GAINS.indexOf(g) - 1)]);
                event.preventDefault()
            } else if (event.key === "]") {
                setSpeed(s => SPEEDS[Math.min(SPEEDS.length - 1, SPEEDS.indexOf(s) + 1)]);
                event.preventDefault()
            } else if (event.key === "[") {
                setSpeed(s => SPEEDS[Math.max(0, SPEEDS.indexOf(s) - 1)]);
                event.preventDefault()
            } else if ((event.key === "r" || event.key === "R") && !event.shiftKey) {
                /* bare R: the orange per-lead reference on or off */
                toggleReference();
                event.preventDefault()
            }
        }

        window.addEventListener("keydown", onKey);

        return () => window.removeEventListener("keydown", onKey)
    }, [toggleReference]);

    /* ---------------------------------------------------------------- */

    /* no rate, or another rate on the server: nothing to place marks on */
    const rateMessage = props.rateProblem === "missing" || props.rateProblem === "mixed"
        ? t(`annotation.save_state.rate_${props.rateProblem}`)
        : labelsStatus === "rate-mismatch"
            ? t("annotation.save_state.rate_mismatch", {server: props.serverRate, signal: sampleRate})
            : props.rateProblem === "loading"
                ? t("annotation.save_state.rate_loading")
                : null;

    if (rateMessage !== null) {
        return (
            <div className={classes.root} data-annotation-surface="">
                <div className={classes.empty}>
                    <div className={classes.empty_title}>{rateMessage}</div>
                </div>
            </div>
        )
    }

    if (!beats.length || !beat) {
        return (
            <div className={classes.root} data-annotation-surface="">
                <div className={classes.empty}>
                    {labelsStatus === "loading" ? (
                        <div className={classes.empty_title}>{t("annotation.labels_loading")}</div>
                    ) : (
                        <React.Fragment>
                            <div className={classes.empty_title}>
                                {t(labelsStatus === "failed" ? "annotation.labels_failed" : "annotation.no_labels")}
                            </div>
                            <div>{t("annotation.no_labels_hint")}</div>
                        </React.Fragment>
                    )}
                </div>
            </div>
        )
    }

    /** A problem in words: "P end must come before QRS start". */
    function orderText(p: OrderProblem): string {
        const name = (k: PointKey) => t(`annotation.point.${k}`);

        return t(`annotation.order.${p.kind}`, {key: name(p.key), other: name(p.other), wave: waveOf(p.key)})
    }

    function dur(a: PointKey, z: PointKey): string {
        const from = beat.points[a];
        const to = beat.points[z];

        return from !== null && to !== null ? `${fmt(to - from)} ms` : "—"
    }

    return (
        <div className={classes.root} data-annotation-surface="">
            <div className={classes.overview}>
                <canvas ref={overviewCanvas} onMouseDown={handleOverviewDown}/>
                <span className={classes.overview_hint}>{t("annotation.overview_hint")}</span>
            </div>

            <div className={classes.work} ref={workRef}>
                {/* as wide as the paper, so the pane scrolls the whole record;
                    the canvas inside stays over the visible part */}
                <div className={classes.paper} style={{width: canvasW, height: canvasH}}>
                    <canvas
                        ref={workCanvas}
                        className={classes.work_canvas}
                        onMouseDown={handleWorkDown}
                        onContextMenu={event => event.preventDefault()}
                        style={{cursor: dragging ? "ew-resize" : "crosshair"}}
                    />
                </div>
            </div>

            {/* The six boundaries, in time order, each stating its own rule. */}
            <div className={classes.bar}>
                {POINT_KEYS.map(key => {
                    const ms = beat.points[key];
                    const isSelected = key === selected;
                    const isUncertain = beat.uncertain.indexOf(key) !== -1;
                    const check = beatCheck ? beatCheck[key] : undefined;
                    const mine = problems.filter(p => p.key === key || p.other === key);
                    const missText = check && !check.ok
                        ? t(check.atLeast ? "annotation.misses_lead_atleast" : "annotation.misses_lead", {
                            lead: leadName(check.lead),
                            ms: check.outByMs,
                        })
                        : "";

                    return (
                        <div
                            key={key}
                            role="group"
                            aria-label={t(`annotation.point.${key}`)}
                            className={[
                                classes.cell,
                                isSelected ? classes.cell__selected : "",
                                ms === null ? classes.cell__absent : "",
                                involved(key) ? classes.cell__order : "",
                            ].join(" ")}
                        >
                        {/* selecting is one button; the nudges are buttons of
                            their own beside it, not spans inside it */}
                        <button
                            type="button"
                            className={classes.cell_select}
                            onClick={() => onSelect(current, key)}
                            aria-pressed={isSelected}
                        >
                            <span className={classes.cell_name}>
                                {t(`annotation.point.${key}`)}
                            </span>
                            <span className={classes.cell_rule}>
                                {t(`annotation.envelope.${ENVELOPE_SIDE[key]}`, {
                                    wave: waveOf(key),
                                })}
                            </span>

                            {ms === null ? (
                                <React.Fragment>
                                    <span className={classes.cell_value}>
                                        {t("annotation.absent")}
                                    </span>
                                    {/* nothing else says how an absent mark
                                        comes back, and arrows used to do
                                        nothing on one */}
                                    <span className={classes.cell_sub}>
                                        {t("annotation.absent_hint")}
                                    </span>
                                </React.Fragment>
                            ) : (
                                <React.Fragment>
                                    <span className={classes.cell_value}>{fmt(ms)} ms</span>
                                    <span className={classes.cell_sub}>
                                        {ms - beat.rPeakMs > 0 ? "+" : ""}
                                        {fmt(ms - beat.rPeakMs)} {t("annotation.from_r")}
                                        {" · "}
                                        {t("annotation.sample")}{" "}
                                        {/*
                                          * The FILE's convention, counted from
                                          * zero — not the model's 1-based
                                          * `msToSample`. This number exists to
                                          * be checked against
                                          * `predictions_global.csv`, and the
                                          * screen saying 401 where the file
                                          * says 400 is an off-by-one the
                                          * technician has no way to resolve.
                                          */}
                                        {msToSampleZeroBased(ms, sampleRate)}
                                    </span>
                                </React.Fragment>
                            )}

                            {/*
                                ONE note line, always there, so the bar keeps
                                its height from beat to beat: the order this
                                mark breaks (red, short — the full sentence is
                                the tooltip and the footer), else the lead it
                                misses (amber), else blank. A full sentence in
                                a sixth of the width was cut to "QRS END MUST
                                C…".
                            */}
                            {mine.length ? (
                                <span className={classes.cell_order} title={mine.map(orderText).join("; ")}>
                                    {t("annotation.order.short")}
                                </span>
                            ) : missText ? (
                                <span className={classes.cell_miss} title={missText}>{missText}</span>
                            ) : (
                                <span className={classes.cell_miss} aria-hidden="true">{"\u00a0"}</span>
                            )}
                        </button>

                        <span className={classes.cell_row}>
                            {/* one sample either way, at the record's rate; an
                                absent mark is added at its starting point */}
                            <button
                                type="button"
                                className={classes.nudge}
                                title={t("annotation.nudge_back")}
                                aria-label={`${t(`annotation.point.${key}`)}: ${t("annotation.nudge_back")}`}
                                onClick={() => onMove(current, key, ms === null
                                    ? startingPoint(beat, key, sampleRate, endMs)
                                    : ms - sampleMs(sampleRate))}
                            >
                                ◀
                            </button>
                            <button
                                type="button"
                                className={classes.nudge}
                                title={t("annotation.nudge_fwd")}
                                aria-label={`${t(`annotation.point.${key}`)}: ${t("annotation.nudge_fwd")}`}
                                onClick={() => onMove(current, key, ms === null
                                    ? startingPoint(beat, key, sampleRate, endMs)
                                    : ms + sampleMs(sampleRate))}
                            >
                                ▶
                            </button>
                            {isUncertain && (
                                <span className={classes.cell_flag}>
                                    {t("annotation.uncertain_short")}
                                </span>
                            )}
                        </span>
                        </div>
                    )
                })}
            </div>

            <div className={classes.controls}>
                <span className={classes.ctl_group}>
                    <span className={classes.ctl_label}>{t("modern.speed")}</span>
                    {SPEEDS.map(s => (
                        <button
                            key={s}
                            type="button"
                            className={`${classes.ctl} ${s === speed ? classes.ctl__on : ""}`}
                            onClick={() => setSpeed(s)}
                        >
                            {s}
                        </button>
                    ))}
                    <span className={classes.ctl_label}>mm/s</span>
                </span>

                <span className={classes.ctl_group}>
                    <span className={classes.ctl_label}>{t("modern.gain")}</span>
                    {GAINS.map(g => (
                        <button
                            key={g}
                            type="button"
                            className={`${classes.ctl} ${g === gain ? classes.ctl__on : ""}`}
                            onClick={() => setGain(g)}
                        >
                            {g}
                        </button>
                    ))}
                    <span className={classes.ctl_label}>mm/mV</span>
                </span>

                {hasReference && (
                    <span className={classes.ctl_group}>
                        <button
                            type="button"
                            className={`${classes.ctl} ${showReference ? classes.ctl__on : ""}`}
                            onClick={toggleReference}
                            aria-pressed={showReference}
                            title={t("annotation.reference.toggle_hint")}
                        >
                            {t(showReference ? "annotation.reference.shown" : "annotation.reference.hidden")}
                        </button>
                    </span>
                )}

                <span className={classes.ctl_group}>
                    <span className={classes.ctl_label}>
                        {Math.round(msPerPx * 100) / 100} {t("annotation.ms_per_px")}
                    </span>
                </span>

                <span className={`${classes.ctl_group} ${classes.foot_spacer}`}>
                    {props.saveRefused !== null && props.saveRefused !== undefined ? (
                        <span className={classes.save_bad}>
                            {t("annotation.save_state.refused", {status: props.saveRefused})}
                        </span>
                    ) : props.saveFailing ? (
                        <span className={classes.save_bad}>{t("annotation.save_state.failing")}</span>
                    ) : null}
                    {saveFailed ? (
                        <span className={classes.save_bad}>{t("annotation.save_failed")}</span>
                    ) : savedAt ? (
                        <span className={classes.save_ok}>
                            {t(savedTo === "server" ? "annotation.saved_server" : "annotation.saved_local", {time: savedAt})}
                        </span>
                    ) : (
                        <span className={classes.ctl_label}>{t("annotation.no_edits")}</span>
                    )}

                </span>
            </div>

            <div className={classes.foot}>
                <span className={classes.foot_group}>
                    <span className={classes.foot_label}>{t("annotation.beat")}</span>
                    <span className={classes.foot_value}>{current} / {beats.length}</span>
                    <span
                        className={`${classes.chip} ${
                            beat.status === "confirmed"
                                ? classes.chip__confirmed
                                : beat.status === "corrected"
                                    ? classes.chip__corrected
                                    : classes.chip__auto
                        }`}
                    >
                        {t(`annotation.status.${beat.usable ? beat.status : "unusable"}`)}
                    </span>
                </span>

                <span className={classes.foot_group}>
                    <span className={classes.foot_label}>{t("annotation.class")}</span>
                    <span className={classes.foot_value}>{beat.beatClass as BeatClass}</span>
                </span>

                <span className={classes.foot_group}>
                    <span className={classes.foot_label}>P</span>
                    <span className={classes.foot_value}>{dur("pStart", "pEnd")}</span>
                </span>

                <span className={classes.foot_group}>
                    <span className={classes.foot_label}>QRS</span>
                    <span className={classes.foot_value}>{dur("qrsStart", "qrsEnd")}</span>
                </span>

                <span className={classes.foot_group}>
                    <span className={classes.foot_label}>QT</span>
                    <span className={classes.foot_value}>{dur("qrsStart", "tEnd")}</span>
                </span>

                {/* The whole record at a glance: how much is still not an
                    envelope. Zero is the condition for the record being done. */}
                <span className={classes.foot_group}>
                    <span className={classes.foot_label}>{t("annotation.contains_all")}</span>
                    <span
                        className={uncontained > 0 ? classes.foot_flag : classes.foot_value}
                    >
                        {uncontained === 0
                            ? t("annotation.all_contained")
                            : t("annotation.beats_uncontained", {count: uncontained})}
                    </span>
                </span>

                {/* how much of the record still breaks the order — and, for the
                    beat in hand, why Enter will not confirm it */}
                <span className={classes.foot_group}>
                    <span className={classes.foot_label}>{t("annotation.order.label")}</span>
                    <span className={outOfOrder > 0 ? classes.foot_flag : classes.foot_value}>
                        {outOfOrder === 0
                            ? t("annotation.order.all_ok")
                            : t("annotation.order.beats", {count: outOfOrder})}
                    </span>
                    {problems.length > 0 && (
                        <span className={classes.foot_flag}>
                            {problems.map(orderText).join("; ")}
                            {" — "}
                            {t("annotation.order.confirm_blocked")}
                        </span>
                    )}
                </span>

                {/* an old browser copy that lost to newer work on the server */}
                {props.conflicts ? (
                    <span className={classes.foot_group}>
                        <span className={classes.foot_flag}>
                            {t("annotation.save_state.conflicts", {count: props.conflicts})}
                        </span>
                    </span>
                ) : null}

                {/* a click the screen could not honour, and why */}
                {hint && (
                    <span className={classes.foot_group}>
                        <span className={classes.foot_flag}>{hint}</span>
                    </span>
                )}

                {/* the orange ticks need a name, or they read as a second set
                    of marks the technician is meant to move; and when hidden,
                    the screen says so, or they look missing from the data */}
                {beat.reference && (
                    <span className={classes.foot_group}>
                        <span className={showReference ? classes.foot_ref : classes.foot_label}>
                            {t(showReference ? "annotation.reference_legend" : "annotation.reference.hidden_legend")}
                        </span>
                    </span>
                )}

                <span className={`${classes.keys} ${classes.foot_spacer}`}>
                    <span><b className={classes.kbd}>Tab</b>{t("annotation.keys.walk")}</span>
                    <span><b className={classes.kbd}>←→</b>{t("annotation.keys.nudge")}</span>
                    <span><b className={classes.kbd}>Enter</b>{t("annotation.keys.confirm")}</span>
                    <span><b className={classes.kbd}>Del</b>{t("annotation.keys.absent")}</span>
                    <span><b className={classes.kbd}>[ ]</b>{t("modern.speed")}</span>
                    <span><b className={classes.kbd}>+ −</b>{t("modern.gain")}</span>
                    {hasReference && (
                        <span><b className={classes.kbd}>R</b>{t("annotation.keys.reference")}</span>
                    )}
                    {/* the approve buttons are off the toolbar here, so the key
                        that replaces them has to be visible somewhere */}
                    <span><b className={classes.kbd}>Ctrl ⏎</b>{t("modern.approve_next")}</span>
                </span>
            </div>
        </div>
    )
}
